import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useNotifications, NOTIFICATION_CATEGORIES } from '../../context/NotificationContext';

// Get notification icon
const getNotificationIcon = (category) => {
  switch (category) {
    case 'COMPLETION':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'UPLOAD':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      );
    case 'APPROVAL':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'COLLABORATION':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    case 'DEADLINE':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'PROGRESS':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      );
    case 'SOULFOOD':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      );
    default:
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      );
  }
};

const ToastNotification = ({ toast, onDismiss }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const shouldDismissRef = useRef(false);
  const categoryInfo = NOTIFICATION_CATEGORIES[toast.category];

  useEffect(() => {
    const duration = 5000; // 5 seconds
    const interval = 50; // Update every 50ms
    const decrement = (interval / duration) * 100;

    intervalRef.current = setInterval(() => {
      if (!isPaused) {
        setProgress(prev => {
          const newProgress = prev - decrement;
          if (newProgress <= 0) {
            clearInterval(intervalRef.current);
            shouldDismissRef.current = true;
            return 0;
          }
          return newProgress;
        });
      }
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused]);
  
  // Handle dismiss when progress reaches 0
  useEffect(() => {
    if (progress === 0 && shouldDismissRef.current) {
      shouldDismissRef.current = false;
      onDismiss(toast.toastId);
    }
  }, [progress, onDismiss, toast.toastId]);

  const handleActionClick = (action) => {
    onDismiss(toast.toastId);
    if (action.navigate) {
      navigate(action.navigate);
    }
  };

  return (
    <div
      className="w-96 rounded-lg overflow-hidden animate-slide-up"
      style={{
        backgroundColor: '#1A1F2E',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        borderLeft: `4px solid ${categoryInfo?.color || '#64748B'}`
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid={`toast-${toast.toastId}`}
    >
      {/* Content */}
      <div className="p-4">
        {/* Category Label */}
        <div className="flex items-center justify-between mb-2">
          <span 
            className="text-[10px] font-bold tracking-wider uppercase"
            style={{ color: categoryInfo?.color || '#64748B' }}
          >
            {categoryInfo?.label || 'NOTIFICATION'}
          </span>
          <button
            onClick={() => onDismiss(toast.toastId)}
            className="p-1 rounded transition-colors hover:bg-white/10"
            style={{ color: '#64748B' }}
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Headline */}
        <div className="flex items-start gap-3">
          <div 
            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ 
              backgroundColor: `${categoryInfo?.color}20` || 'rgba(100, 116, 139, 0.2)',
              color: categoryInfo?.color || '#64748B'
            }}
          >
            {getNotificationIcon(toast.category)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white leading-tight">
              {toast.headline}
            </p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed line-clamp-2">
              {toast.detail}
            </p>
          </div>
        </div>

        {/* Actions */}
        {toast.actions && toast.actions.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            {toast.actions.slice(0, 2).map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleActionClick(action)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={action.primary ? {
                  backgroundColor: categoryInfo?.color || '#64748B',
                  color: '#0F172A'
                } : {
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#94A3B8'
                }}
                onMouseEnter={(e) => {
                  if (!action.primary) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.color = '#FFFFFF';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!action.primary) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#94A3B8';
                  }
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="h-1 w-full" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
        <div
          className="h-full transition-all"
          style={{
            width: `${progress}%`,
            backgroundColor: categoryInfo?.color || '#64748B',
            transition: isPaused ? 'none' : 'width 50ms linear'
          }}
        />
      </div>
    </div>
  );
};

const ToastContainer = () => {
  const { toasts, removeToast } = useNotifications();

  return (
    <div 
      className="fixed bottom-6 right-6 z-50 flex flex-col-reverse gap-3"
      data-testid="toast-container"
    >
      {toasts.map((toast) => (
        <ToastNotification
          key={toast.toastId}
          toast={toast}
          onDismiss={removeToast}
        />
      ))}
    </div>
  );
};

export default ToastContainer;
